<template>
    <div class="bg-white">
        <Banner :title="t('about.companyProfile')" :subtitle="t('about.coreValuesSubtitle')" />

        <section class="py-20">
            <div class="container mx-auto px-4">
                <div class="flex flex-col md:flex-row gap-16 items-center">
                    <div class="flex-1">
                        <div class="text-center mb-12">
                            <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.companyProfile') }}</h2>
                        </div>
                        <div class="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                            <div class="space-y-4">
                                <p class="text-[#212121]">{{ t('about.established') }}</p>
                                <p class="text-[#212121]">{{ t('about.formerName') }}</p>
                                <p class="text-[#212121]">{{ t('about.firstNewsprint') }}</p>
                                <p class="text-[#212121]">{{ t('about.listed') }}</p>
                                <p class="text-[#212121]">{{ t('about.firstListed') }}</p>
                                <p class="text-[#212121]">{{ t('about.firstYibinListed') }}</p>
                                <p class="text-[#212121]">{{ t('about.wuliangyeSubsidiary') }}</p>
                                <p class="text-[#212121]">{{ t('about.firstNewMeterial') }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="flex-1">
                        <img src="/images/company/2.jpg" alt="宜宾纸业厂区全景" class="w-full rounded-lg shadow-lg">
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-4">
                <div class="flex flex-col md:flex-row gap-16 items-center">
                    <div class="flex-1">
                        <img src="/images/capacitySituation/1.jpg" alt="产能情况图" class="w-full rounded-lg shadow-lg">
                    </div>
                    <div class="flex-1">
                        <div class="text-center mb-12">
                            <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.capacity') }}</h2>
                        </div>
                        <div class="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                            <div class="space-y-4">
                                <p class="text-[#212121]">{{ t('about.mainCapacity') }}</p>
                                <p class="text-[#212121]">{{ t('about.managedCapacity') }}</p>
                                <p class="text-[#212121]">{{ t('about.newMaterialCapacity') }}</p>
                                <p class="text-[#212121]">{{ t('about.bambooMoldingCapacity') }}</p>
                                <p class="text-[#212121]">{{ t('about.celluloseCapacity') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.historyTitle') }}</h2>
                    <p class="text-[#8d6e63]">{{ t('about.historySubtitle') }}</p>
                </div>
                <div class="relative max-w-4xl mx-auto">
                    <div class="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gray-200"></div>
                    <div class="space-y-16">
                        <div v-for="(item, index) in timelineItems" :key="index" class="relative">
                            <div class="absolute left-1/2 transform -translate-x-1/2 w-5 h-5 bg-green-500 rounded-full z-10"></div>
                            <div :class="['w-1/2', index % 2 === 0 ? 'ml-auto pl-8' : 'mr-auto pr-8']">
                                <div class="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                                    <h3 class="text-xl font-bold text-[#005005] mb-4">{{ item.year }}</h3>
                                    <p class="text-[#212121] mb-4">{{ t(item.text) }}</p>
                                    <img :src="item.image" :alt="item.year + '年图'" class="w-full rounded-lg">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.futureDevelopment') }}</h2>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    <div v-for="(point, index) in futurePoints" :key="index" 
                         class="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <div class="text-4xl font-bold text-green-500 mb-4">{{ String(index + 1).padStart(2, '0') }}</div>
                        <h3 class="text-xl font-semibold text-[#005005]">{{ t(point) }}</h3>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.coreValues') }}</h2>
                    <p class="text-[#8d6e63]">{{ t('about.coreValuesSubtitle') }}</p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    <div v-for="(value, index) in coreValues" :key="index" 
                         class="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <div class="text-4xl text-green-500 mb-4">
                            <i :class="value.icon"></i>
                        </div>
                        <h3 class="text-xl font-semibold text-[#005005] mb-4">{{ t(value.title) }}</h3>
                        <p class="text-[#212121] mb-2">{{ t(value.line1) }}</p>
                        <p class="text-[#212121]">{{ t(value.line2) }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.certificationsTitle') }}</h2>
                    <p class="text-[#8d6e63]">{{ t('about.certificationsSubtitle') }}</p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div v-for="(cert, index) in certifications" :key="index" 
                         class="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <img :src="cert.image" :alt="cert.title" class="w-full max-w-[200px] h-auto mx-auto mb-4">
                        <h3 class="text-xl font-semibold text-[#005005] mb-4">{{ t(cert.title) }}</h3>
                        <p class="text-[#212121]">{{ t(cert.description) }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.honorsTitle') }}</h2>
                    <p class="text-[#8d6e63]">{{ t('about.honorsSubtitle') }}</p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div v-for="(honor, index) in honors" :key="index" 
                         class="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <img :src="honor.image" :alt="honor.title" class="w-full max-w-[200px] h-auto mx-auto mb-4">
                        <h3 class="text-xl font-semibold text-[#005005] mb-4">{{ t(honor.title) }}</h3>
                        <p class="text-[#212121]">{{ t(honor.description) }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('about.productHonorsTitle') }}</h2>
                    <p class="text-[#8d6e63]">{{ t('about.productHonorsSubtitle') }}</p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div v-for="(honor, index) in productHonors" :key="index" 
                         class="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <img :src="honor.image" :alt="honor.title" class="w-full max-w-[200px] h-auto mx-auto mb-4">
                        <h3 class="text-xl font-semibold text-[#005005] mb-4">{{ t(honor.title) }}</h3>
                        <p class="text-[#212121]">{{ t(honor.description) }}</p>
                    </div>
                </div>
            </div>
        </section>

        <CTA
          :title="t('about.cta.title')"
          :description="t('about.cta.description')"
          :buttonText="t('about.cta.buttonText')"
          buttonLink="/contact"
          backgroundImage="/images/56.png"
        />
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { onMounted } from 'vue'
import Banner from '../components/Banner.vue'
import CTA from '../components/CTA.vue'

const { t } = useI18n()

onMounted(() => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    })
})

const timelineItems = [
    { year: '1944', text: 'about.historyItems.1944', image: '/images/historyTimeline/ChuangShiRen.jpg' },
    { year: '1945', text: 'about.historyItems.1945', image: '/images/historyTimeline/DiYiZhangBaoZhi.jpg' },
    { year: '1950', text: 'about.historyItems.1950', image: '/images/historyTimeline/1950-1.jpg' },
    { year: '1961', text: 'about.historyItems.1961', image: '/images/historyTimeline/1961.jpg' },
    { year: '1966', text: 'about.historyItems.1966', image: '/images/historyTimeline/1966.jpg' },
    { year: '1988', text: 'about.historyItems.1988', image: '/images/historyTimeline/1988-1.jpg' },
    { year: '1997', text: 'about.historyItems.1997', image: '/images/historyTimeline/1997.jpg' },
    { year: '2012', text: 'about.historyItems.2012', image: '/images/historyTimeline/2012-1.jpg' },
    { year: '2015', text: 'about.historyItems.2015', image: '/images/historyTimeline/2015.png' },
    { year: '2018', text: 'about.historyItems.2018_1', image: '/images/historyTimeline/2018.jpg' },
    { year: '2018', text: 'about.historyItems.2018_2', image: '/images/historyTimeline/2018-2.jpg' },
    { year: '2025', text: 'about.historyItems.2025', image: '/images/company/3.jpg' }
]

const futurePoints = [
    'about.future.point1',
    'about.future.point2',
    'about.future.point3',
    'about.future.point4'
]

const coreValues = [
    {
        icon: 'fas fa-bullseye',
        title: 'about.values.mission',
        line1: 'about.values.missionLine1',
        line2: 'about.values.missionLine2'
    },
    {
        icon: 'fas fa-eye',
        title: 'about.values.vision',
        line1: 'about.values.visionLine1',
        line2: 'about.values.visionLine2'
    },
    {
        icon: 'fas fa-gem',
        title: 'about.values.coreValues',
        line1: 'about.values.coreValuesLine1',
        line2: 'about.values.coreValuesLine2'
    },
    {
        icon: 'fas fa-handshake',
        title: 'about.values.philosophy',
        line1: 'about.values.philosophyLine1',
        line2: 'about.values.philosophyLine2'
    }
]

const certifications = [
    {
        image: '/images/certification/30.jfif',
        title: 'about.certificationItems.fsc',
        description: 'about.certificationItems.fscDesc'
    },
    {
        image: '/images/certification/25.jfif',
        title: 'about.certificationItems.iso9001',
        description: 'about.certificationItems.iso9001Desc'
    },
    {
        image: '/images/certification/26.jfif',
        title: 'about.certificationItems.iso14001',
        description: 'about.certificationItems.iso14001Desc'
    },
    {
        image: '/images/certification/27.jfif',
        title: 'about.certificationItems.medical',
        description: 'about.certificationItems.medicalDesc'
    },
    {
        image: '/images/certification/28.jfif',
        title: 'about.certificationItems.antibacterial',
        description: 'about.certificationItems.antibacterialDesc'
    },
    {
        image: '/images/certification/WeiShengAnQuanChanPinRenZhengZhengShu.jpg',
        title: 'about.certificationItems.safety',
        description: 'about.certificationItems.safetyDesc'
    }
]

const honors = [
    {
        image: '/images/Honors/Country/3.GuoJiaLvSeGongChang.jpg',
        title: 'about.honorItems.greenFactory',
        description: 'about.honorItems.greenFactoryDesc'
    },
    {
        image: '/images/Honors/Country/1.GuoJiaLinYeZhongDianLongTouQiYe.jpg',
        title: 'about.honorItems.forestryLeader',
        description: 'about.honorItems.forestryLeaderDesc'
    },
    {
        image: '/images/Honors/Country/2.ZhongGuoYouXiuNongChanPinQiYePinPai.jpg',
        title: 'about.honorItems.agriculturalProduct',
        description: 'about.honorItems.agriculturalProductDesc'
    },
    {
        image: '/images/Honors/Country/4.QuanGuoQingGongHangYeXianJinJiTi.jpg',
        title: 'about.honorItems.lightIndustry',
        description: 'about.honorItems.lightIndustryDesc'
    },
    {
        image: '/images/Honors/Province/ZhuChanPinShiQiangQiYe.png',
        title: 'about.honorItems.bambooProduct',
        description: 'about.honorItems.bambooProductDesc'
    },
    {
        image: '/images/Honors/Province/4.SiChuanShengZhuZiZhiJiangZaoZhiGongChengJiShuYanJiuZhongXin.jpg',
        title: 'about.honorItems.engineeringCenter',
        description: 'about.honorItems.engineeringCenterDesc'
    }
]

const productHonors = [
    {
        image: '/images/Honors/Country/5.NeZhaBaoZhuang2025HangYeJiangXinChanPin.jpg',
        title: 'about.productHonorItems.industryExcellence',
        description: 'about.productHonorItems.neZhaExcellence'
    },
    {
        image: '/images/Honors/Country/6.MoDiKaBaoZhuang2025HangYeJiangXinChanPin.jpg',
        title: 'about.productHonorItems.industryExcellence',
        description: 'about.productHonorItems.moDiKaExcellence'
    },
    {
        image: '/images/Honors/Country/MoDiKa2025ZhuBoHuiJinJiang.jpg',
        title: 'about.productHonorItems.bambooExpoGold',
        description: 'about.productHonorItems.moDiKaGold'
    },
    {
        image: '/images/Honors/Country/2020NianShangHaiGuoJiZhuBoLanHuiJinJiang.jpg',
        title: 'about.productHonorItems.shanghaiExpoGold',
        description: 'about.productHonorItems.jinZhuGold'
    },
    {
        image: '/images/Honors/Country/2018NianShangHaiGuoJiZhuBoLanHuiJinJiang.jpg',
        title: 'about.productHonorItems.shanghaiExpoGold',
        description: 'about.productHonorItems.quanZhuGold'
    },
    {
        image: '/images/Honors/Country/ZhongGuoXiBuLinYeChanYeBoLanHuiJinJiang.jpg',
        title: 'about.productHonorItems.westernExpoGold',
        description: 'about.productHonorItems.jinZhuWesternGold'
    }
]
</script>

<style scoped>
/* 删除所有CSS样式，因为已经使用Tailwind CSS类替换 */
</style> 